import {
  SessionReplayEventsManager as AmplitudeSessionReplayEventsManager,
  EventsStore,
  EventType,
  StoreType,
} from '../typings/session-replay';

import { SessionReplayJoinedConfig } from '../config/types';
import { MAX_SINGLE_EVENT_SIZE } from '../constants';
import { getStorageSize } from '../helpers';
import { PayloadBatcher, SessionReplayTrackDestination } from '../track-destination';
import { SessionReplayEventsIDBStore } from './events-idb-store';
import { InMemoryEventsStore } from './events-memory-store';

export type EventsManagerWithBeacon<Type extends EventType> = AmplitudeSessionReplayEventsManager<Type, string> & {
  /**
   * Returns current pending events (since last flush) for synchronous access on page exit.
   * Used to populate a sendBeacon payload when the page is unloading.
   */
  getBeaconEvents(): string[];
  /**
   * Drops all pending beacon events. Used when the session is decided to be below
   * the min duration threshold so its events don't leak into a later session's beacon.
   */
  dropPendingBeaconEvents(): void;
  trackDestination: SessionReplayTrackDestination;
};

export const createEventsManager = async <Type extends EventType>({
  config,
  minInterval,
  maxInterval,
  type,
  payloadBatcher,
  storeType,
  trackDestinationWorkerScript,
  shouldSend,
}: {
  config: SessionReplayJoinedConfig;
  type: Type;
  minInterval?: number;
  maxInterval?: number;
  payloadBatcher?: PayloadBatcher;
  storeType: StoreType;
  trackDestinationWorkerScript?: string;
  shouldSend?: () => boolean;
}): Promise<EventsManagerWithBeacon<Type>> => {
  const trackDestination = new SessionReplayTrackDestination({
    ...config,
    loggerProvider: config.loggerProvider,
    payloadBatcher,
    workerScript: trackDestinationWorkerScript,
  });

  const getMemoryStore = (): EventsStore<number> => {
    return new InMemoryEventsStore({
      loggerProvider: config.loggerProvider,
      maxInterval,
      minInterval,
    });
  };

  let lastKnownDeviceId: string | undefined;
  let usingIdbStore = false;
  let store!: EventsStore<number>;

  const switchToMemoryStore = async () => {
    if (!usingIdbStore) return;
    usingIdbStore = false;
    config.loggerProvider.warn('IDB store is experiencing repeated failures; falling back to in-memory event store.');
    const sequences = lastKnownDeviceId ? await store.getSequencesToSend() : undefined;
    store = getMemoryStore();
    if (sequences && lastKnownDeviceId) {
      const deviceId = lastKnownDeviceId;
      sequences.forEach((seq) => {
        sendEventsList({ sequenceId: seq.sequenceId, events: seq.events, sessionId: seq.sessionId, deviceId });
      });
    }
  };

  const getIdbStoreOrFallback = async (): Promise<EventsStore<number>> => {
    const idb = await SessionReplayEventsIDBStore.new(type, {
      loggerProvider: config.loggerProvider,
      minInterval,
      maxInterval,
      apiKey: config.apiKey,
      onPersistentFailure: () => {
        void switchToMemoryStore();
      },
    });
    if (!idb) {
      config.loggerProvider.log('Failed to initialize idb store, falling back to memory store.');
      return getMemoryStore();
    }
    usingIdbStore = true;
    return idb;
  };

  store = storeType === 'idb' ? await getIdbStoreOrFallback() : getMemoryStore();

  // Beacon buffer: a sliding window of pending (unsent) event strings for synchronous
  // access on page exit. Uses an absolute index counter to correctly handle concurrent
  // async flushes without losing events added between the flush call and its resolution.
  const beaconBuffer: string[] = [];
  let beaconWindowStart = 0; // absolute index of the first element in beaconBuffer

  const advanceBeaconWindow = (upToAbsoluteIdx: number) => {
    if (upToAbsoluteIdx <= beaconWindowStart) return;
    const trimCount = Math.min(upToAbsoluteIdx - beaconWindowStart, beaconBuffer.length);
    if (trimCount > 0) {
      beaconBuffer.splice(0, trimCount);
      beaconWindowStart = upToAbsoluteIdx;
    }
  };

  /**
   * Immediately sends events to the track destination.
   */
  const sendEventsList = ({
    events: rawEvents,
    sessionId,
    deviceId,
    sequenceId,
  }: {
    events: string[];
    sessionId: string | number;
    deviceId: string;
    sequenceId?: number;
  }) => {
    // Backstop for events that entered IDB before the per-event size guard in
    // addCompressedEventToManager (e.g. stored by a previous SDK version or via
    // storeCurrentSequence/sendStoredEvents which bypass the capture-time check).
    // Compare UTF-8 byte size, not JS char count, to match the server-side limit.
    const sizedEvents = rawEvents.map((e) => ({ event: e, bytes: new Blob([e]).size }));
    const oversized = sizedEvents.filter((s) => s.bytes > MAX_SINGLE_EVENT_SIZE);
    if (oversized.length > 0) {
      config.loggerProvider.warn(
        `Dropping ${oversized.length} oversized event(s) from session replay sequence before send. Sizes: ${oversized
          .map((s) => `${Math.round(s.bytes / 1024)} KB`)
          .join(
            ', ',
          )}. If this recurs, please open a GitHub issue at https://github.com/amplitude/Amplitude-TypeScript/issues or contact Amplitude support.`,
      );
    }
    const events =
      oversized.length > 0
        ? sizedEvents.filter((s) => s.bytes <= MAX_SINGLE_EVENT_SIZE).map((s) => s.event)
        : rawEvents;
    if (events.length === 0) {
      store.cleanUpSessionEventsStore(sessionId, sequenceId).catch((e) => {
        config.loggerProvider.warn('Failed to clean up session replay events store:', e);
      });
      return;
    }

    if (config.debugMode) {
      getStorageSize()
        .then(({ totalStorageSize, percentOfQuota, usageDetails }) => {
          config.loggerProvider.debug(
            `Total storage size: ${totalStorageSize} KB, percentage of quota: ${percentOfQuota}%, usage details: ${usageDetails}`,
          );
        })
        .catch(() => {
          // swallow error
        });
    }

    trackDestination.sendEventsList({
      events: events,
      sessionId: sessionId,
      flushMaxRetries: config.flushMaxRetries,
      apiKey: config.apiKey,
      deviceId: deviceId,
      sampleRate: config.sampleRate,
      serverZone: config.serverZone,
      version: config.version,
      type,
      onComplete: async () => {
        await store.cleanUpSessionEventsStore(sessionId, sequenceId);
        return;
      },
    });
  };

  const sendCurrentSequenceEvents = ({ sessionId, deviceId }: { sessionId: number; deviceId: string }) => {
    lastKnownDeviceId = deviceId;
    // Evaluate shouldSend synchronously before the async store read. asyncSetSessionId
    // updates sessionStartTime immediately after calling sendEvents(), so by the time
    // storeCurrentSequence resolves the start time would reflect the new session and
    // the elapsed check would compute ~0ms, silently dropping the previous session's events.
    if (shouldSend && !shouldSend()) return;
    // Snapshot the absolute end-index before the async store read so that any events
    // pushed after this point are NOT considered sent and remain in the beacon buffer.
    const snapshotAbsIdx = beaconWindowStart + beaconBuffer.length;
    store
      .storeCurrentSequence(sessionId)
      .then((currentSequence) => {
        if (currentSequence) {
          advanceBeaconWindow(snapshotAbsIdx);
          sendEventsList({
            sequenceId: currentSequence.sequenceId,
            events: currentSequence.events,
            sessionId: currentSequence.sessionId,
            deviceId,
          });
        }
      })
      .catch((e) => {
        config.loggerProvider.warn('Failed to get current sequence of session replay events for session:', e);
      });
  };

  const sendStoredEvents = async ({ deviceId }: { deviceId: string }) => {
    lastKnownDeviceId = deviceId;
    const sequencesToSend = await store.getSequencesToSend();
    if (!sequencesToSend?.length) {
      return;
    }
    config.loggerProvider.log(`Draining ${sequencesToSend.length} stored sequence(s) from previous session.`);
    sequencesToSend.forEach((sequence) => {
      sendEventsList({
        sequenceId: sequence.sequenceId,
        events: sequence.events,
        sessionId: sequence.sessionId,
        deviceId,
      });
    });
  };

  const addEvent = ({
    event,
    sessionId,
    deviceId,
  }: {
    event: { type: Type; data: string };
    sessionId: number;
    deviceId: string;
  }) => {
    lastKnownDeviceId = deviceId;
    // Capture shouldSend synchronously before the async store write, for the same
    // reason as sendCurrentSequenceEvents: asyncSetSessionId updates sessionStartTime
    // synchronously, so evaluating inside the .then() would use the new session's
    // start time and compute ~0ms elapsed, silently dropping a valid batch.
    const canSend = !shouldSend || shouldSend();
    // Record the absolute index of this event in the beacon buffer before the async
    // store operation. If a batch split occurs, we advance the window up to (but not
    // including) this event so that it starts the next pending window.
    const absIdx = beaconWindowStart + beaconBuffer.length;
    beaconBuffer.push(event.data);
    store
      .addEventToCurrentSequence(sessionId, event.data)
      .then((sequenceToSend) => {
        if (sequenceToSend) {
          if (!canSend) {
            // The split atomically moved events to sequencesToSend; without cleanup
            // they would be unconditionally replayed on next page load via
            // sendStoredEvents, bypassing the min session duration gate. The split
            // batch must also be dropped from the beacon buffer so it isn't sent
            // via sendBeacon on page unload (potentially attributed to a later session).
            store.cleanUpSessionEventsStore(sequenceToSend.sessionId, sequenceToSend.sequenceId).catch((e) => {
              config.loggerProvider.warn('Failed to clean up dropped session replay sequence:', e);
            });
            advanceBeaconWindow(absIdx);
            return;
          }
          // Events before absIdx belong to the split batch being sent; advance window.
          advanceBeaconWindow(absIdx);
          sendEventsList({
            sequenceId: sequenceToSend.sequenceId,
            events: sequenceToSend.events,
            sessionId: sequenceToSend.sessionId,
            deviceId,
          });
        }
      })
      .catch((e) => {
        config.loggerProvider.warn('Failed to add event to session replay capture:', e);
      });
  };

  async function flush(useRetry = false) {
    return trackDestination.flush(useRetry);
  }

  const getBeaconEvents = (): string[] => [...beaconBuffer];

  const dropPendingBeaconEvents = () => {
    advanceBeaconWindow(beaconWindowStart + beaconBuffer.length);
  };

  return {
    sendCurrentSequenceEvents,
    addEvent,
    sendStoredEvents,
    flush,
    getBeaconEvents,
    dropPendingBeaconEvents,
    trackDestination,
  };
};
